import { desdeIso } from "../../lib/calendario";

/**
 * Cómo se muestra el detalle de una OT.
 *
 * Vive aparte de la pantalla porque son reglas con bordes —el `null`, el
 * `"0.00"`, la fecha de calendario que no es un instante— y una pantalla no se
 * puede poner en un test sin un browser.
 */

import { numero, pesos } from "../../lib/formato";

/** Lo que se muestra donde no hay dato. Uno solo, en un lugar. */
const VACIO = "—";

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

/**
 * A `Date`, distinguiendo la fecha de calendario del instante.
 *
 * `fecha_pactada_entrega` es una columna `date` y llega `"2026-08-22"`:
 * `new Date` de eso es medianoche **UTC**, que en Buenos Aires es el día
 * anterior a las 21:00 — la entrega se mostraría un día antes. `desdeIso`
 * (`lib/calendario.ts`) la arma con los componentes, en hora local.
 *
 * Un `timestamp` sí es un instante y va por `new Date`, que lo pasa a la zona
 * de quien mira, que es lo correcto para «cuándo se imprimió esto».
 */
function aDate(valor: unknown): Date | null {
  if (typeof valor !== "string" || valor === "") return null;
  const conHora = valor.includes("T");
  const d = conHora ? new Date(valor) : desdeIso(valor);
  if (!d || Number.isNaN(d.getTime())) return null;
  return d;
}

const dosDigitos = (n: number) => String(n).padStart(2, "0");

/** `22 ago 2026`. `—` si no hay dato o no es una fecha. */
export function fecha(valor: unknown): string {
  const d = aDate(valor);
  if (!d) return VACIO;
  return `${dosDigitos(d.getDate())} ${MESES[d.getMonth()]} ${d.getFullYear()}`;
}

/** `22 ago 2026 14:30`. Para los `timestamp`, donde la hora importa. */
export function fechaHora(valor: unknown): string {
  const d = aDate(valor);
  if (!d) return VACIO;
  return `${fecha(valor)} ${dosDigitos(d.getHours())}:${dosDigitos(d.getMinutes())}`;
}

/** El `numeric` de Postgres llega como string; a número, o `null`. */
function aNumero(valor: unknown): number | null {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : null;
  if (typeof valor !== "string" || valor.trim() === "") return null;
  const n = Number(valor);
  return Number.isFinite(n) ? n : null;
}

/**
 * `2,5 h`. Cero es un dato —esa OT no tiene horas cargadas—, así que se
 * imprime; lo que no se sabe es `—`.
 */
export function horas(valor: unknown): string {
  const n = aNumero(valor);
  if (n === null) return VACIO;
  return `${numero(n)} h`;
}

/**
 * `$1.234,50`.
 *
 * Sin dato va `—` y no `$0,00`: a quien no tiene `produccion.precios` el worker
 * le manda `total: null` (ADR 0015), y un cero ahí diría que la OT no vale
 * nada en lugar de que no lo puede ver.
 */
export function importe(valor: unknown): string {
  const n = aNumero(valor);
  if (n === null) return VACIO;
  return pesos(n);
}

/**
 * El aviso rojo del encabezado, o `null` si la OT no es urgente.
 *
 * `esUrgente` y `fechaLimite` son columnas independientes: se puede marcar
 * urgente sin comprometer una fecha, y ahí el aviso va igual. Es lo que hace el
 * `JobDetail` de `imprenta`.
 */
export function avisoDeUrgencia(orden: Record<string, unknown>): string | null {
  if (orden.esUrgente !== true) return null;
  const limite = aDate(orden.fechaLimite);
  if (!limite) return "URGENTE";
  return `URGENTE — Límite: ${dosDigitos(limite.getDate())}/${dosDigitos(limite.getMonth() + 1)}`;
}
